import apiClient from '@/services/axios'
import store from 'store'

/**
 * Reserved Stock API
 *
 * Dual-mode: real API (default) or mock.
 * Set VUE_APP_USE_RESERVED_STOCK_MOCK=true to use mock data for local testing.
 *
 * Endpoints needed (backend):
 *   GET  /inventoryv2/query/reserved-stock           — get reserved stock
 *   PUT  /inventoryv2/cmd/reserved-stock          — update reserved stock (backend determines RESERVED_IN/OUT)
 *   GET  /inventoryv2/query/reserved-stock/history    — get change history
 */

const USE_REAL_API = process.env.VUE_APP_USE_RESERVED_STOCK_MOCK !== 'true'

// ---------------------------------------------------------------------------
// Mock data store (in-memory, resets on page refresh)
// ---------------------------------------------------------------------------

/** @type {Map<string, number>} key = `${catalog_id}__${warehouse_id}` */
const mockReservedStore = new Map()

/** @type {Map<string, Array>} key = `${catalog_id}__${warehouse_id}` */
const mockHistoryStore = new Map()

function mockKey(catalogId, warehouseId) {
  return `${catalogId}__${warehouseId}`
}

function delay(ms = 400) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// ---------------------------------------------------------------------------
// Mock implementations
// ---------------------------------------------------------------------------

async function mockGetReservedStock({ catalog_id, warehouse_id }) {
  await delay(200)
  const key = mockKey(catalog_id, warehouse_id)
  return {
    data: {
      catalog_id,
      warehouse_id,
      reserved_stock: mockReservedStore.get(key) ?? 0,
    },
  }
}

async function mockUpdateReservedStock({ catalog_id, warehouse_id, business_id, reserved_stock, reason }) {
  await delay(500)

  const key = mockKey(catalog_id, warehouse_id)
  const previousValue = mockReservedStore.get(key) ?? 0
  const newValue = Number(reserved_stock)

  if (!Number.isFinite(newValue) || newValue < 0) {
    throw { response: { data: { message: 'Reserved stock must be 0 or greater' } } }
  }

  mockReservedStore.set(key, newValue)

  // Determine movement type based on delta
  const movementType = newValue > previousValue ? 'RESERVED_IN' : 'RESERVED_OUT'
  const quantity = Math.abs(newValue - previousValue)

  // Add to history
  const history = mockHistoryStore.get(key) || []
  history.unshift({
    id: `mock-${Date.now()}`,
    catalog_id,
    warehouse_id,
    movement_type: movementType,
    quantity,
    previous_reserved: previousValue,
    new_reserved: newValue,
    reason: reason || '-',
    user_name: 'Current User (mock)',
    created_at: new Date().toISOString(),
  })
  mockHistoryStore.set(key, history)

  return {
    data: {
      catalog_id,
      warehouse_id,
      reserved_stock: newValue,
      movement_type: movementType,
      quantity,
    },
  }
}

async function mockGetReservedStockHistory({ catalog_id, warehouse_id, page = 1, size = 10 }) {
  await delay(300)

  const key = mockKey(catalog_id, warehouse_id)
  const allHistory = mockHistoryStore.get(key) || []
  const start = (page - 1) * size
  const end = start + size

  return {
    data: {
      data: allHistory.slice(start, end),
      total_row: allHistory.length,
    },
  }
}

// ---------------------------------------------------------------------------
// Real API implementations
// ---------------------------------------------------------------------------

const getLang = () => {
  const locale = store.get('app.settings.locale') || 'id'
  return locale.split('-')[0]
}

function realGetReservedStock({ catalog_id, warehouse_id, business_id }) {
  return apiClient({
    method: 'get',
    url: '/inventoryv2/query/reserved-stock',
    headers: {
      'Business-Id': business_id,
      'Accept-Language': getLang(),
    },
    params: {
      catalog_id,
      warehouse_id,
      business_id,
    },
  })
}

function realUpdateReservedStock({ catalog_id, warehouse_id, business_id, reserved_stock, reason }) {
  return apiClient({
    method: 'put',
    url: '/inventoryv2/cmd/reserved-stock',
    headers: {
      'Business-Id': business_id,
      'Accept-Language': getLang(),
    },
    data: {
      catalog_id,
      warehouse_id,
      business_id,
      reserved_stock,
      reason,
    },
  })
}

function realGetReservedStockHistory({ catalog_id, warehouse_id, business_id, page, size }) {
  return apiClient({
    method: 'get',
    url: '/inventoryv2/query/reserved-stock/history',
    headers: {
      'Business-Id': business_id,
      'Accept-Language': getLang(),
    },
    params: {
      catalog_id,
      warehouse_id,
      business_id,
      page,
      size,
    },
  })
}

async function mockBulkUpdateReservedStock({ business_id, items }) {
  await delay(1000)
  
  const results = []
  for (const item of items) {
    const { sku, warehouse_id, reserved_stock } = item
    const key = `${sku}__${warehouse_id}`
    mockReservedStore.set(key, reserved_stock)
    results.push({ sku, warehouse_id, reserved_stock, status: 'success' })
  }
  
  return {
    data: {
      message: 'Bulk update successful',
      results,
    },
  }
}

function realBulkUpdateReservedStock({ business_id, items }) {
  return apiClient({
    method: 'post',
    url: '/inventoryv2/cmd/reserved-stock/bulk-update',
    headers: {
      'Business-Id': business_id,
      'Accept-Language': getLang(),
    },
    data: {
      business_id,
      items,
    },
  })
}

// ---------------------------------------------------------------------------
// Exported API (auto-switches between mock and real)
// ---------------------------------------------------------------------------

/**
 * Get reserved stock for a specific catalog + warehouse
 *
 * @param {{ catalog_id: string, warehouse_id: string, business_id: string }} params
 * @returns {Promise<{ data: { catalog_id: string, warehouse_id: string, reserved_stock: number } }>}
 */
export const getReservedStock = USE_REAL_API
  ? realGetReservedStock
  : mockGetReservedStock

/**
 * Update reserved stock value.
 * Backend determines RESERVED_IN or RESERVED_OUT based on delta with current value.
 *
 * @param {{ catalog_id: string, warehouse_id: string, business_id: string, reserved_stock: number, reason: string }} params
 * @returns {Promise<{ data: { catalog_id: string, warehouse_id: string, reserved_stock: number, movement_type: string, quantity: number } }>}
 */
export const updateReservedStock = USE_REAL_API
  ? realUpdateReservedStock
  : mockUpdateReservedStock

/**
 * Get history/log of reserved stock changes
 *
 * @param {{ catalog_id: string, warehouse_id: string, business_id: string, page?: number, size?: number }} params
 * @returns {Promise<{ data: { data: Array, total_row: number } }>}
 */
export const getReservedStockHistory = USE_REAL_API
  ? realGetReservedStockHistory
  : mockGetReservedStockHistory

/**
 * Bulk update reserved stock values via JSON (Legacy/Internal)
 * 
 * @param {{ business_id: string, items: Array<{ sku: string, warehouse_id: string, reserved_stock: number }> }} params
 * @returns {Promise<any>}
 */
export const bulkUpdateReservedStock = (params) => {
  if (!USE_REAL_API) return mockBulkUpdateReservedStock(params)
  return realBulkUpdateReservedStock(params)
}

/**
 * Upload Bulk Reserved Stock (File-based)
 */
export function uploadReservedStock({ file, businessId, userId, userName }) {
  const formData = new FormData()
  formData.append('file', file)
  return apiClient({
    method: 'post',
    url: '/inventoryv2/cmd/reserved-stock/upload',
    headers: {
      'Business-Id': businessId,
      'Accept-Language': getLang(),
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
}

/**
 * Check Upload Status
 */
export function checkUploadStatus(processId) {
  return apiClient({
    method: 'get',
    url: `/inventoryv2/cmd/reserved-stock/upload-process/${processId}`,
    headers: {
      'Accept-Language': getLang(),
    },
  })
}

/**
 * Download Annotated Result
 */
export function downloadAnnotatedResult(processId) {
  return apiClient({
    method: 'get',
    url: `/inventoryv2/cmd/reserved-stock/uploaded-file/${processId}`,
    responseType: 'blob',
    headers: {
      'Accept-Language': getLang(),
    },
  })
}

/**
 * Download file from URL (used by Reserved Stock History)
 */
export function downloadByUrl(fileUrl) {
  // If the URL starts with /reserved-stock, it likely needs the gateway prefix /inventoryv2/cmd
  const url = fileUrl.startsWith('/reserved-stock') 
    ? `/inventoryv2/cmd${fileUrl}` 
    : fileUrl

  return apiClient({
    method: 'get',
    url,
    responseType: 'blob',
    headers: {
      'Accept-Language': getLang(),
    },
  })
}

/**
 * Download Template
 */
export function downloadReservedStockTemplate(params) {
  return apiClient({
    method: 'get',
    url: '/inventoryv2/cmd/reserved-stock/download-template',
    params,
    responseType: 'blob',
    headers: {
      'Accept-Language': getLang(),
    },
  })
}

/**
 * Get Upload History (Batch list)
 */
export function getReservedStockUploadHistory(params) {
  return apiClient({
    method: 'get',
    url: '/inventory-cmd/reserved-stock/upload-history',
    params,
    headers: {
      'Accept-Language': getLang(),
    },
  })
}

/**
 * Get Reserved Stock History (Global/Search)
 */
export function getReservedStockHistoryDetail(params) {
  return apiClient({
    method: 'get',
    url: '/inventory/reserved-stock/history',
    params,
    headers: {
      'Accept-Language': getLang(),
    },
  })
}

/**
 * Get Upload History Details (Records in a specific batch)
 */
export function getReservedStockUploadDetail(process_id, params) {
  return apiClient({
    method: 'get',
    url: `/inventory-cmd/reserved-stock/upload-history/${process_id}/details`,
    params,
    headers: {
      'Accept-Language': getLang(),
    },
  })
}
